import { Action, Dispatch, MiddlewareAPI } from 'redux'
import { putRequest, urlBuilder } from 'tfs.front-end-utils/requests'

import ClientPassengerInterface from '../../interfaces/client/Passenger'
import ClientPassengerSSRInterface from '../../interfaces/client/PassengerSSR'
import ReduxStoreInterface from '../../interfaces/client/ReduxStore'

import staticData from '../../config/staticData'

import { addPassengersReferences } from '../../actions/bookings'
import { UPDATE_BOOKING } from '../../actions/bookings/updateBooking'
import { addNotification } from '../../actions/notifications'
import {
  addPassengerSSR,
  addPassengers,
  passengersFetched,
  removePassengerSSR,
} from '../../actions/passengers'

import getLabels from '../../utils/getLabels'
import {
  buildGenericNotificationFromErrors,
  buildServerNotification,
  dispatchErrorNotifications,
  dispatchSuccessNotification,
} from '../../utils/notifications'
import {
  buildClientPassenger,
  buildServerPassenger,
  getPassengersRefs,
} from '../../utils/passengers'
import getAuthenticationHeaders from '../../utils/requests/generateAuthorizationHeaders'
import { SSRIsNotConfirmed, SSRIsSetForDeletion } from '../../utils/ssrs'
import { customTourOperatorParameters } from '../../utils/tourOperators'

export interface CreateBookingAction extends Action {
  passengers: ClientPassengerInterface[]
  flightId: string
}

export default (store: MiddlewareAPI) => (next: Dispatch) => async (
  action: CreateBookingAction
) => {
  next(action)

  if (action.type !== UPDATE_BOOKING) {
    return
  }

  const {
    endpoints: {
      allotments: { url, headers },
    },
  } = staticData
  const { flightId, passengers } = action

  const dispatch = store.dispatch
  const state: ReduxStoreInterface = store.getState()

  const addReferenceNumbersToPassenger = (
    passenger: ClientPassengerInterface
  ) => {
    const {
      thirdPartyBookingNumber,
      transaviaBookingNumber,
    } = state.passengers.byId[passenger.id]

    return {
      ...passenger,
      thirdPartyBookingNumber,
      transaviaBookingNumber,
    }
  }

  const serverFormattedPassengers = passengers
    .map(addReferenceNumbersToPassenger)
    .map(buildServerPassenger)

  try {
    const fixedUrl = url.endsWith('/') ? url : `${url}/`
    const requestUrl = urlBuilder(
      `${fixedUrl}${flightId}/passengers`,
      customTourOperatorParameters()
    )

    const authHeaders = await getAuthenticationHeaders(headers)

    const bookingsResponse = await putRequest(requestUrl, {
      headers: { ...headers, ...authHeaders },
      body: JSON.stringify(serverFormattedPassengers),
    })

    if (bookingsResponse.passengers.length) {
      const clientFormattedPassengers = bookingsResponse.passengers.map(
        buildClientPassenger
      )
      dispatch(addPassengers(clientFormattedPassengers))
      dispatch(passengersFetched(clientFormattedPassengers))

      const passengersRefs = getPassengersRefs(
        passengers,
        clientFormattedPassengers
      )

      if (Object.keys(passengersRefs).length) {
        store.dispatch(addPassengersReferences(passengersRefs))
      }

      clientFormattedPassengers.forEach(
        (passenger: ClientPassengerInterface) => {
          const passengerSSRsFromStore = state.passengerSSRs.byId[passenger.id]
          if (!passengerSSRsFromStore) {
            return
          }

          const dispatchRemovePassengerSSR = (
            passengerSSR: ClientPassengerSSRInterface
          ) => {
            dispatch(
              removePassengerSSR(passenger.id, passengerSSR.passengerSSRId)
            )
          }

          const dispatchAddPassengerSSR = (
            passengerSSR: ClientPassengerSSRInterface
          ) => {
            dispatch(
              addPassengerSSR(
                passenger.id,
                passengerSSR.passengerSSRId,
                passengerSSR.ssrId,
                {
                  isConfirmed: true,
                  isAbleToDelete: true,
                  ssrNumber: passengerSSR.ssrNumber,
                },
                passengerSSR.dimensions
              )
            )
          }

          passengerSSRsFromStore
            .filter(SSRIsSetForDeletion)
            .forEach(dispatchRemovePassengerSSR)

          passengerSSRsFromStore
            .filter(SSRIsNotConfirmed)
            .forEach(dispatchAddPassengerSSR)
        }
      )
    }

    if (bookingsResponse.errors.length) {
      dispatchErrorNotifications(dispatch, [
        ...bookingsResponse.errors,
        buildGenericNotificationFromErrors(bookingsResponse.errors),
      ])
    } else {
      dispatchSuccessNotification(dispatch, getLabels('passengers_updated'))
    }
  } catch (error) {
    dispatch(addNotification(buildServerNotification(error)))
  }
}
